import { LineItem, QuoteVariant } from '@pelicargo/types';
import * as utils from '@pelicargo/utils';

import { trpc, TrpcOutputs } from '../config/trpc';
import { Quote } from './quotes/useQuoteHelper';
import { useRequest } from './request/useRequest';
import { useTargetAccount } from './useTargetAccount';

export type QuoteVariantWithLineItems = QuoteVariant & { line_items: LineItem[] };
export type QuoteVariantWithLineItemsPossibleInland = TrpcOutputs['getQuote']['quote_variants'][0];

type UseQuoteVariantHelper = {
  id?: number;

  hasInlandService: boolean;

  exclusiveVariableRate: number;

  allInVariableRate: number;
  airFreightAllInVariableRate: number;
  inlandAllInVariableRate: number;

  screeningRate: number;
  inlandTotal: number;
  airFreightTotal: number;
  totalCost: number;
  otherFeesTotal: number;

  airlineMarkupTotal: number;
  inlandMarkupTotal: number;
  totalCostWithoutMarkup: number;

  extraInlandMarkups: LineItem[];
};

export const calculateTotalCostWithoutMarkup = (quoteVariant: QuoteVariantWithLineItemsPossibleInland) => {
  const airFreightTotal = utils.totalCost(quoteVariant?.line_items || []);
  const inlandTotal = utils.totalCost(quoteVariant?.inland_service_quote_variant?.line_items || []);
  const totalCostWithoutMarkup = airFreightTotal + (inlandTotal || 0);

  return { airFreightTotal, inlandTotal, totalCostWithoutMarkup };
};

export const useQuoteVariantHelper = (
  quoteVariant: QuoteVariantWithLineItemsPossibleInland,
  quote: Quote,
): UseQuoteVariantHelper => {
  const hasInlandService = !!quoteVariant?.inland_service_quote_variant_id;
  const { targetAccount } = useTargetAccount();
  const [request] = useRequest(quote?.request_id);

  const isFPChargesEnabled =
    targetAccount?.is_fulfillment_partner &&
    targetAccount?.id === quote?.fulfillment_partner_configuration?.account_as_fulfillment_partner_id;
  const { data } = trpc.getMarkupTotalsFromQuoteVariant.useQuery(
    { id: quoteVariant?.id },
    { enabled: isFPChargesEnabled },
  );
  const { data: sellRateLineItems } = trpc.getSellRateLineItems.useQuery(
    { id: quoteVariant?.id },
    { enabled: isFPChargesEnabled },
  );

  // Total Sums
  const otherFeesTotal = utils.getOtherFeesTotal(quoteVariant?.line_items || []);
  const { totalCostWithoutMarkup, airFreightTotal, inlandTotal } = calculateTotalCostWithoutMarkup(quoteVariant);
  const inlandSellRateTotal = utils.totalCost(sellRateLineItems?.inlandLineItems || []);

  const totalCost = totalCostWithoutMarkup + (data?.airlineMarkupTotal || 0) + (data?.inlandMarkupTotal || 0);

  // Variable Rates
  const exclusiveVariableRate = utils.exclusiveVariableRate(quoteVariant?.line_items || [], quote?.chargeable_weight);
  const screeningRate = utils.screeningFee(quoteVariant?.line_items || []);
  const inlandAllInVariableRate = utils.allInVariableRate(
    quoteVariant?.inland_service_quote_variant?.line_items || [],
    request?.chargeable_weight,
  );
  const airFreightAllInVariableRate = utils.allInVariableRate(
    quoteVariant?.line_items || [],
    request?.chargeable_weight,
  );
  const allInVariableRate = airFreightAllInVariableRate + (inlandAllInVariableRate || 0);

  return {
    id: quoteVariant?.id,

    hasInlandService,

    exclusiveVariableRate,

    allInVariableRate,
    airFreightAllInVariableRate,
    inlandAllInVariableRate,

    screeningRate,
    inlandTotal: inlandTotal - inlandSellRateTotal,
    airFreightTotal,
    otherFeesTotal,
    totalCost,

    airlineMarkupTotal: data?.airlineMarkupTotal,
    inlandMarkupTotal: data?.inlandMarkupTotal,
    totalCostWithoutMarkup: totalCostWithoutMarkup - inlandSellRateTotal,

    extraInlandMarkups: sellRateLineItems?.inlandLineItems || [],
  };
};
